// Single source of truth for classifying feedback/telemetry events into the
// GOOD / BAD / ISSUES / REQ buckets. Used by both the nav Analytics popover
// (navigation-bar-horizontal.tsx) and the full list (admin/FeedbackSection.tsx)
// so the two never drift apart.

export type FeedbackKind = 'positive' | 'negative' | 'issue' | 'request' | 'other';

/**
 * `tabContext.report_type` written by the feedback form. Tickets stored before
 * the Bugs → Issues rename carry `'bug'`; they classify as `'issue'`.
 */
export type ReportType = 'issue' | 'feature' | 'bug';

/** Minimal shape needed to classify an event — real event types are supersets. */
export interface ClassifiableFeedbackEvent {
  event_name?: string;
  feedback_value?: string;
  feedback?: string;
  event_data?: { tabContext?: { report_type?: string }; [k: string]: unknown };
}

export function classifyFeedbackEvent(event: ClassifiableFeedbackEvent): FeedbackKind {
  const name = (event.event_name ?? '').toLowerCase();
  if (name === 'chat_result_positive_feedback') return 'positive';
  if (name === 'chat_result_negative_feedback') return 'negative';
  if (name === 'ticket_submission') {
    return event.event_data?.tabContext?.report_type === 'feature' ? 'request' : 'issue';
  }
  const val = (event.feedback_value ?? event.feedback ?? '').toLowerCase();
  if (['thumbs_up', 'positive', 'like', 'good'].some(k => val.includes(k))) return 'positive';
  if (['thumbs_down', 'negative', 'dislike', 'bad'].some(k => val.includes(k))) return 'negative';
  // Unrecognized telemetry type — don't force it into the thumbs-down bucket.
  return 'other';
}
